import { compact } from 'lodash';

import type {
  GetUnitRosterLiteResponse,
  UnitRosterLeaderRow,
  UnitRosterYouthRow,
} from '@appTypes/webscript';
import { MemberType } from '@modules/roster-lite/constants';

/**
 * Domain model for a member of the `SBLUnitRosterLiteReport`
 * Adds what the API does not provide:
 * - `memberType`: derived from which array the row came from (youth vs leader).
 * - `positions`: the leader's comma-joined `position` string split into a list
 *
 * Youth rows may carry `sb_user_id` (the ESB userId), used to link to the
 * youth-details route; rows without it are not linkable.
 */
export type UnitRosterMember = Partial<
  UnitRosterYouthRow & UnitRosterLeaderRow
> & {
  item_number: string;
  member_id: string;
  name: string;
  memberType: MemberType;
  positions: string[];
};

function splitPositions(position: string): string[] {
  return compact(position.split(',').map(p => p.trim()));
}

export function fromYouthRow(row: UnitRosterYouthRow): UnitRosterMember {
  return { ...row, memberType: MemberType.Youth, positions: [] };
}

export function fromLeaderRow(row: UnitRosterLeaderRow): UnitRosterMember {
  return {
    ...row,
    memberType: MemberType.Adult,
    positions: splitPositions(row.position),
  };
}

export function membersFromReport(
  report: GetUnitRosterLiteResponse,
): UnitRosterMember[] {
  return [
    ...report.youth_members.map(fromYouthRow),
    ...report.leader_members.map(fromLeaderRow),
  ];
}

export function isYouth(m: UnitRosterMember): boolean {
  return m.memberType === MemberType.Youth;
}
